import { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck, Search, FileDown, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const statusColor = {
  Pending: "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  "Under Review": "text-blue-400 bg-blue-500/10 border-blue-500/40",
  Approved: "text-green-500 bg-green-500/10 border-green-500/40",
  Rejected: "text-red-500 bg-red-500/10 border-red-500/40",
  Resolved: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40",
};
const STATUSES = ["Pending", "Under Review", "Approved", "Rejected", "Resolved"];

const empty = { serial_number: "", claim_date: "", claim_reason: "", status: "Pending", remarks: "" };
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function WarrantyClaims({ embedded = false }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fComp, setFComp] = useState("all");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState({ ...empty, claim_date: todayISO() });
  const [fetched, setFetched] = useState(null);

  const canManage = ["owner", "manager"].includes(user.role);

  const load = (query = q) => api.get(`/warranty-claims${query ? `?q=${encodeURIComponent(query)}` : ""}`).then(r => setList(r.data));
  useEffect(() => {
    load("");
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();
  const companies = [...new Set(items.map(i => i.company).filter(Boolean))].sort();
  const itemCat = (itemId) => items.find(i => i.id === itemId)?.category || "";
  const itemComp = (itemId) => items.find(i => i.id === itemId)?.company || "";
  const filtered = list.filter(c =>
    (fCat === "all" || itemCat(c.item_id) === fCat) &&
    (fComp === "all" || itemComp(c.item_id) === fComp)
  );
  const pg = usePagination(filtered, q);

  const fetchBySerial = async (serial) => {
    if (!serial) { setFetched(null); return; }
    try {
      const { data } = await api.get(`/warranties/by-serial/${encodeURIComponent(serial)}`);
      setFetched(data);
    } catch { setFetched(null); }
  };

  const save = async () => {
    try {
      const payload = { ...f, remarks: f.remarks || null };
      if (editId) await api.patch(`/warranty-claims/${editId}`, payload);
      else await api.post("/warranty-claims", payload);
      toast.success(editId ? "Claim updated" : "Claim created");
      setF({ ...empty, claim_date: todayISO() }); setFetched(null); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (c) => {
    setF({ serial_number: c.serial_number, claim_date: c.claim_date, claim_reason: c.claim_reason, status: c.status, remarks: c.remarks || "" });
    setFetched({ item_name: c.item_name, item_code: c.item_code, customer_name: c.customer_name, purchase_date: c.purchase_date, sale_date: c.sale_date, start_date: c.start_date, expiry_date: c.expiry_date });
    setEditId(c.id); setOpen(true);
  };

  const del = async (c) => {
    if (!confirm(`Delete claim ${c.claim_number}? This cannot be undone.`)) return;
    try { await api.delete(`/warranty-claims/${c.id}`); toast.success("Claim deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const fetchPdf = async (url, filename, download) => {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = objUrl; a.download = filename; a.click();
      } else window.open(objUrl, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const claimDialog = canManage && (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF({ ...empty, claim_date: todayISO() }); setFetched(null); setEditId(null); } }}>
      <DialogTrigger asChild>
        <Button data-testid="add-claim-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
          <Plus className="w-4 h-4 mr-1"/> New Claim
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Warranty Claim</DialogTitle></DialogHeader>
        <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Serial Number *</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="claim-serial" value={f.serial_number} onChange={(e) => { setF(x => ({ ...x, serial_number: e.target.value })); setFetched(null); }} className="rounded-sm bg-secondary border-border font-mono flex-1" placeholder="e.g. BAT-2026-001"/>
              <Button type="button" data-testid="claim-fetch" variant="outline" onClick={() => fetchBySerial(f.serial_number)} className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0"><Download className="w-4 h-4"/></Button>
            </div>
          </div>
          {fetched && (
            <div data-testid="claim-warranty-info" className="border border-border rounded-sm p-3 text-xs font-mono space-y-1 bg-secondary/40">
              <div className="flex justify-between"><span className="text-muted-foreground">Item</span><span>{fetched.item_name} {fetched.item_code ? `· ${fetched.item_code}` : ""}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{fetched.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Purchase Date</span><span>{fetched.purchase_date || "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sale Date</span><span>{fetched.sale_date || "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Warranty Start</span><span>{fetched.start_date || "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Warranty Expiry</span><span className="text-primary">{fetched.expiry_date || "-"}</span></div>
              {fetched.computed_status && <div className="flex justify-between"><span className="text-muted-foreground">Warranty Status</span><span>{fetched.computed_status}</span></div>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Claim Date *</Label>
              <Input data-testid="claim-date" type="date" value={f.claim_date} onChange={(e) => setF(x => ({ ...x, claim_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Claim Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="claim-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Claim Reason *</Label>
            <Textarea data-testid="claim-reason" value={f.claim_reason} onChange={(e) => setF(x => ({ ...x, claim_reason: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2} placeholder="Describe the issue…"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Remarks (optional)</Label>
            <Textarea data-testid="claim-remarks" value={f.remarks} onChange={(e) => setF(x => ({ ...x, remarks: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
          </div>
          <Button data-testid="save-claim-btn" onClick={save} disabled={!f.serial_number || !f.claim_date || !f.claim_reason} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
            {editId ? "Update Claim" : "Save Claim"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className={embedded ? "" : "p-8"}>
      {!embedded && (
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3"/> Inventory · Warranty Master</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Warranty Claims</h1>
          </div>
          {claimDialog}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search (serial, customer, claim #, item)</Label>
          <div className="relative mt-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input data-testid="claim-search" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Type to search…" className="pl-7 rounded-sm bg-secondary border-border h-9"/>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
          <Select value={fCat} onValueChange={setFCat}>
            <SelectTrigger data-testid="claim-filter-category" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Company</Label>
          <Select value={fComp} onValueChange={setFComp}>
            <SelectTrigger data-testid="claim-filter-company" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="claims-export-pdf" size="sm" variant="outline" onClick={() => fetchPdf(`${API}/warranty-claims/export.pdf${q ? `?q=${encodeURIComponent(q)}` : ""}`, "warranty_claims_export.pdf", true)} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
          <Button data-testid="claims-print" size="sm" variant="outline" onClick={() => fetchPdf(`${API}/warranty-claims/export.pdf${q ? `?q=${encodeURIComponent(q)}` : ""}`, "", false)} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
          {embedded && claimDialog}
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Claim #</th>
              <th className="text-left p-3">Claim Date</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Serial #</th>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Item Code</th>
              <th className="text-left p-3">Purchase Inv #</th>
              <th className="text-left p-3">Purchase Date</th>
              <th className="text-right p-3">Duration</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-left p-3">Sale Inv #</th>
              <th className="text-left p-3">Sale Date</th>
              <th className="text-left p-3">Start Date</th>
              <th className="text-left p-3">Expiry Date</th>
              <th className="text-left p-3">Claim Reason</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={17} className="p-6 text-center text-muted-foreground">No warranty claims found</td></tr>}
            {pg.pageItems.map(c => (
              <tr key={c.id} data-testid={`claim-row-${c.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{c.claim_number}</td>
                <td className="p-3 font-mono text-xs">{c.claim_date}</td>
                <td className="p-3 text-xs">{itemComp(c.item_id) || "-"}</td>
                <td className="p-3 font-mono text-xs">{c.serial_number}</td>
                <td className="p-3 text-xs">{c.item_name}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{c.item_code || "-"}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{c.purchase_txn_number || "-"}</td>
                <td className="p-3 font-mono text-xs">{c.purchase_date || "-"}</td>
                <td className="p-3 text-right font-mono text-xs">{c.duration_months ? `${c.duration_months} mo` : "-"}</td>
                <td className="p-3 text-xs">{c.customer_name}</td>
                <td className="p-3 font-mono text-[10px] text-muted-foreground">{c.sale_txn_number || "-"}</td>
                <td className="p-3 font-mono text-xs">{c.sale_date || "-"}</td>
                <td className="p-3 font-mono text-xs">{c.start_date || "-"}</td>
                <td className="p-3 font-mono text-xs">{c.expiry_date || "-"}</td>
                <td className="p-3 text-xs max-w-48 truncate" title={c.claim_reason}>{c.claim_reason}{c.remarks && <div className="text-muted-foreground italic mt-0.5">{c.remarks}</div>}</td>
                <td className="p-3"><span data-testid={`claim-status-${c.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[c.status]}`}>{c.status}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`claim-pdf-${c.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(`${API}/warranty-claims/${c.id}/pdf`, `claim_${c.claim_number}.pdf`, true)} className="h-7 w-7 p-0" title="Download PDF"><FileDown className="w-3 h-3"/></Button>
                  <Button data-testid={`claim-print-${c.id}`} size="sm" variant="ghost" onClick={() => fetchPdf(`${API}/warranty-claims/${c.id}/pdf`, "", false)} className="h-7 w-7 p-0" title="Print"><Printer className="w-3 h-3"/></Button>
                  {canManage && <Button data-testid={`edit-claim-${c.id}`} size="sm" variant="ghost" onClick={() => edit(c)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canManage && <Button data-testid={`del-claim-${c.id}`} size="sm" variant="ghost" onClick={() => del(c)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
